import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Stack,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  styled,
  CircularProgress
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  RuleOutlined as RuleOutlinedIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { useScrollableFilters } from '../hooks/useScrollableFilters';
import ScrollableFilters from './ScrollableFilters';
import InfoBanner from '../../common/InfoBanner';
import CategoryChip from '../../common/CategoryChip';
import ResponsiveListTable from '../../common/ResponsiveListTable';
// Export moved to RulesManager

const FilterToggleButton = styled(Button)(({ theme, selected }) => ({
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '0.875rem',
  height: 36,
  padding: '6px 16px',
  borderRadius: theme.shape.borderRadius,
  transition: 'all 0.2s ease',
  whiteSpace: 'nowrap',
  border: '1px solid #d1d5db',
  minWidth: 'auto',
  boxShadow: 'none',
  ...(selected ? {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      borderColor: theme.palette.primary.dark,
      transform: 'translateY(-1px)',
    }
  } : {
    backgroundColor: '#f9fafb',
    color: '#374151',
    borderColor: '#d1d5db',
    '&:hover': {
      backgroundColor: '#f3f4f6',
      borderColor: '#9ca3af',
      transform: 'translateY(-1px)',
    }
  })
}));

const ConditionChip = styled(Chip)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  fontSize: '0.8125rem',
  height: 20,
  backgroundColor: '#ffe6ef',
  color: '#F41468',
  border: 'none',
  fontWeight: 500,
  '& .MuiChip-label': { fontWeight: 500, fontSize: '0.8125rem', lineHeight: 1 }
}));

const OrChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  fontWeight: 'bold',
  fontSize: '0.8125rem',
  marginBottom: theme.spacing(0.5),
}));

const PriorityIndicator = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 22,
  height: 22,
  borderRadius: '50%',
  background: '#F41468',
  color: '#fff',
  fontWeight: 700,
  fontSize: '0.75rem',
  lineHeight: 1,
}));

const ActionButton = ({ children, sx, ...props }) => (
  <Button
    sx={{
      borderRadius: 3,
      textTransform: 'none',
      fontWeight: 500,
      boxShadow: 'none',
      padding: '8px 24px',
      ...sx
    }}
    {...props}
  >
    {children}
  </Button>
);

// Sort UI removed — ProgressSortIcon and sort menu are no longer used

const RulesTable = ({
  groupedRules,
  rules,
  categories,
  selected,
  onSelect,
  applyingRules,
  rulesCategoryFilter,
  onRulesCategoryFilterChange,
  onRunAll,
  onRunSelected,
  onRunSingle,
  onCreateRule,
  onEditRule,
  onDeleteRule,
  formatCondition,
  getCategoryName,
  sorting,
  onDownloadRules,
  onImportRules,
  onSelectAll,
  onDeleteSelected,
  onConsolidate,
  aiConsolidating
}) => {
  const { rulesSort, handleSortChange } = sorting;

  const rulesFiltersScroll = useScrollableFilters();

  // Flatten the grouped rules (respecting the category filter) into one list
  // for the table; each rule carries its category for the Category column.
  const visibleRules = groupedRules
    .filter(group => rulesCategoryFilter === '' || group.category.id === rulesCategoryFilter)
    .flatMap(group => group.rules.map(r => ({ ...r, __category: group.category })));

  const getConditionGroups = (rule) =>
    (rule.conditionGroups && rule.conditionGroups.length > 0)
      ? rule.conditionGroups
      : [{ conditions: rule.conditions || [] }];

  const getAllConditions = (rule) => getConditionGroups(rule).flatMap(g => g.conditions || []);

  const conditionSummary = (rule) => {
    const all = getAllConditions(rule);
    return all.length > 0 ? formatCondition(all[0]) : '';
  };

  const sortedVisibleRules = [...visibleRules].sort((a, b) => {
    if (rulesSort.type === 'priority') {
      return rulesSort.direction === 'asc' ? a.priority - b.priority : b.priority - a.priority;
    }
    let cmp;
    if (rulesSort.type === 'category') {
      cmp = (a.__category.name || '').localeCompare(b.__category.name || '');
    } else if (rulesSort.type === 'conditions') {
      cmp = conditionSummary(a).localeCompare(conditionSummary(b));
    } else {
      cmp = (a.name || '').localeCompare(b.name || '');
    }
    return rulesSort.direction === 'asc' ? cmp : -cmp;
  });

  const visibleRuleIds = visibleRules.map(r => r._id);

  const handleHeaderSort = (column) => {
    const dir = rulesSort.type === column && rulesSort.direction === 'asc' ? 'desc' : 'asc';
    handleSortChange(column, dir);
  };

  const renderCategoryFilters = () => (
    <Box sx={{ px: 3, pt: 2, pb: 1 }}>
      <ScrollableFilters
        scrollRef={rulesFiltersScroll.scrollRef}
        showLeftScroll={rulesFiltersScroll.showLeftScroll}
        showRightScroll={rulesFiltersScroll.showRightScroll}
        onScrollLeft={rulesFiltersScroll.scrollLeft}
        onScrollRight={rulesFiltersScroll.scrollRight}
      >
        <FilterToggleButton
          selected={rulesCategoryFilter === ''}
          onClick={() => onRulesCategoryFilterChange('')}
          sx={{ typography: 'body3' }}
        >
          All ({rules.length})
        </FilterToggleButton>
        {groupedRules.map(group => (
          <FilterToggleButton
            key={group.category.id}
            selected={rulesCategoryFilter === group.category.id}
            onClick={() => onRulesCategoryFilterChange(group.category.id)}
            sx={{ typography: 'body3' }}
          >
            {group.category.name} ({group.rules.length})
          </FilterToggleButton>
        ))}
      </ScrollableFilters>
    </Box>
  );

  // Conditions cell: a truncated one-line summary that expands (via the
  // shared table's click-to-expand) into the full chip list with OR groups.
  const renderConditions = (rule, expanded) => {
    const allConditions = getAllConditions(rule);
    if (!expanded) {
      const extraCount = Math.max(0, allConditions.length - 1);
      return (
        <>
          {allConditions.length > 0 ? formatCondition(allConditions[0]) : 'No conditions'}
          {extraCount > 0 && (
            <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400, fontSize: '0.8125rem' }}>
              {` +${extraCount} more condition${extraCount === 1 ? '' : 's'}`}
            </Box>
          )}
        </>
      );
    }
    return (
      <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.5, alignItems: 'center' }}>
        {getConditionGroups(rule).map((groupItem, groupIndex) => (
          <React.Fragment key={groupIndex}>
            {groupIndex > 0 && (
              <OrChip label="OR" size="small" sx={{ fontWeight: 'bold', height: 20, mb: 0, alignSelf: 'center' }} />
            )}
            {(groupItem.conditions || []).map((condition, condIndex) => (
              <ConditionChip
                key={condIndex}
                label={formatCondition(condition)}
                size="small"
                sx={{ px: 1 }}
              />
            ))}
          </React.Fragment>
        ))}
      </Stack>
    );
  };

  const renderRulesList = () => {
    if (sortedVisibleRules.length === 0) {
      return (
        <InfoBanner>
          No rules found for this filter.
        </InfoBanner>
      );
    }

    return (
      <ResponsiveListTable
        items={sortedVisibleRules}
        getId={(r) => r._id}
        columns={[
          {
            id: 'priority', label: 'Priority',
            render: r => <PriorityIndicator>{r.priority}</PriorityIndicator>
          },
          {
            id: 'category', label: 'Category',
            render: r => (
              <CategoryChip
                label={r.__category.name}
                color={r.__category.color}
                sx={{ fontSize: '0.75rem', height: 20 }}
              />
            )
          },
          {
            id: 'conditions', label: 'Conditions',
            render: renderConditions,
            sx: { fontWeight: 500, maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis' }
          }
        ]}
        sortBy={rulesSort.type}
        sortDirection={rulesSort.direction}
        onSort={handleHeaderSort}
        selectedIds={selected}
        onToggleSelect={onSelect}
        onSelectAll={(checked) => onSelectAll(visibleRuleIds, checked)}
        actions={[
          { key: 'run', label: 'Run', icon: <PlayArrowIcon sx={{ fontSize: 18 }} />, onClick: (r) => { if (!applyingRules) onRunSingle(r._id); } },
          { key: 'edit', label: 'Edit', icon: <EditIcon sx={{ fontSize: 18 }} />, onClick: ({ __category, ...r }) => onEditRule(r) },
          { key: 'delete', label: 'Delete', icon: <DeleteIcon sx={{ fontSize: 18 }} />, onClick: (r) => onDeleteRule(r._id), danger: true }
        ]}
        mobilePrimary={(r, expanded) => {
          const all = getAllConditions(r);
          if (!all.length) return 'No conditions';
          if (!expanded) {
            const extra = all.length - 1;
            return `${formatCondition(all[0])}${extra > 0 ? ` +${extra} more` : ''}`;
          }
          return all.map(formatCondition).join('  •  ');
        }}
        mobileSecondary={(r) => {
          const count = getAllConditions(r).length;
          return `Priority ${r.priority} • ${count} condition${count === 1 ? '' : 's'}`;
        }}
        mobileChips={(r) => (
          <CategoryChip
            label={r.__category.name}
            color={r.__category.color}
            sx={{ fontSize: '0.75rem', height: 20 }}
          />
        )}
      />
    );
  };

  return (
    <Paper elevation={2} sx={{ p: 0, borderRadius: 3, width: '100%', maxWidth: '100%', mb: 4, overflow: 'hidden' }}>
      <Box sx={{
        px: { xs: 2, sm: 3 },
        pt: { xs: 1, sm: 1.5 },
        pb: { xs: 1, sm: 1.5 },
        borderTopLeftRadius: 12,
        borderTopRightRadius: 12,
        bgcolor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: { xs: 1, sm: 2 },
        flexWrap: 'wrap',
      }}>
        <Typography variant="h6" fontWeight="bold" sx={{ m: 0, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="span" sx={{ display: 'inline-flex', color: 'text.secondary', '& svg': { fontSize: '1.1rem' } }}>
            <RuleOutlinedIcon />
          </Box>
          Rules
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: { xs: 0.5, sm: 1.5, md: 2 }, alignItems: 'center', justifyContent: 'flex-end' }}>
          <ActionButton variant="contained" size="small" startIcon={<AddIcon />} onClick={onCreateRule} sx={{ display: { xs: 'none', sm: 'inline-flex' }, minWidth: { sm: 100, md: 120 }, px: { sm: 2, md: 2.5 }, py: 1, whiteSpace: 'nowrap' }}>
            Add
          </ActionButton>
          <IconButton size="small" onClick={onCreateRule} sx={{ display: { xs: 'inline-flex', sm: 'none' }, color: '#fff', bgcolor: '#F41468', borderRadius: 2, width: 36, height: 36, '&:hover': { bgcolor: '#c2185b' } }}>
            <AddIcon fontSize="small" />
          </IconButton>

          <ActionButton variant="contained" size="small" startIcon={<PlayArrowIcon />} onClick={() => onRunSelected(selected)} disabled={applyingRules || !selected.length} sx={{ display: { xs: 'none', sm: 'inline-flex' }, minWidth: { sm: 100, md: 120 }, px: { sm: 2, md: 2.5 }, py: 1, bgcolor: '#F41468', whiteSpace: 'nowrap', '&:hover': { bgcolor: '#c2185b' } }}>
            Run
          </ActionButton>
          <IconButton size="small" onClick={() => onRunSelected(selected)} disabled={applyingRules || !selected.length} sx={{ display: { xs: 'inline-flex', sm: 'none' }, color: '#fff', bgcolor: '#F41468', borderRadius: 2, width: 36, height: 36, '&:hover': { bgcolor: '#c2185b' }, '&.Mui-disabled': { bgcolor: '#f8bbd0', color: '#fff' } }}>
            <PlayArrowIcon fontSize="small" />
          </IconButton>

          <Tooltip
            title={
              selected.length > 1
                ? `Merge overlapping rules among the ${selected.length} selected`
                : selected.length === 1
                  ? 'Select at least two rules to consolidate them, or clear the selection to consolidate all rules'
                  : 'Merge overlapping rules into one. With nothing selected, all rules are reviewed.'
            }
          >
            <span>
              <IconButton onClick={onConsolidate} disabled={aiConsolidating || rules.length < 2 || selected.length === 1} sx={{ color: '#fff', bgcolor: 'secondary.main', borderRadius: 2, width: 36, height: 36, '&:hover': { bgcolor: 'secondary.dark' }, '&.Mui-disabled': { bgcolor: '#e1bee7', color: '#fff' } }}>
                {aiConsolidating ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Download all rules as CSV">
            <IconButton onClick={onDownloadRules} disabled={rules.length === 0} sx={{ color: '#fff', bgcolor: '#F41468', borderRadius: 2, width: 36, height: 36, '&:hover': { bgcolor: '#c2185b' }, '&.Mui-disabled': { bgcolor: '#f8bbd0', color: '#fff' } }}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Import rules from CSV">
            <IconButton onClick={onImportRules} sx={{ color: '#fff', bgcolor: '#F41468', borderRadius: 2, width: 36, height: 36, '&:hover': { bgcolor: '#c2185b' } }}>
              <UploadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Divider />
      
      {/* Category Filters */}
      {renderCategoryFilters()}
      
      {/* Rules List */}
      <Box sx={{ mb: 2, px: 3, pt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, minHeight: 28 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
            Showing {visibleRules.length} rule{visibleRules.length !== 1 ? 's' : ''}
          </Typography>
          {selected.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<DeleteIcon />}
              onClick={onDeleteSelected}
              sx={{
                ml: 1,
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.8125rem',
                color: '#c2185b',
                borderColor: '#f8bbd0',
                px: 1.5,
                py: 0.25,
                minHeight: 0,
                '&:hover': { backgroundColor: '#fce4ec', borderColor: '#F41468' }
              }}
            >
              Delete ({selected.length})
            </Button>
          )}
        </Box>
        {renderRulesList()}
      </Box>
    </Paper>
  );
};

export default RulesTable;